/**
 * ADR-0177 — starter-template CTA visibility helpers.
 *
 * The "Load a starter template" affordance appears in the Guardrails
 * empty state ONLY when EVERY guard passes:
 *   (a) the DRAFT has zero rules,
 *   (b) EITHER there is no active policy, OR the active policy itself
 *       has zero rules (a merchant published their draft back down to
 *       blank guardrails), OR the active policy is the untouched
 *       ADR-0080 baseline (matches on version label AND on the
 *       rules-fingerprint of the ADR-0080 constant),
 *   (c) the caller's role is `owner` or `admin`.
 *
 * The client-side check exists so a merchant with any authored work
 * never sees the CTA offer to silently overwrite it. The server also
 * gates the publish route (RBAC + confirm_replace_active + TOCTOU), so
 * this is UX not authorization.
 *
 * The BASELINE_RULES constant hand-mirrors `DEFAULT_POLICY_RULES` in
 * `services/api/src/sites/default-policy.ts` (ADR-0080) — this file
 * can't import server code, so it's a second copy, not a re-export.
 * The SHA-256 fingerprint of BOTH copies is pinned by a matching pair
 * of drift-guard tests: `apps/dashboard/src/__tests__/baseline-
 * fingerprint-parity.test.tsx` (this side) and `services/api/test/
 * sites/policy-templates-dashboard-mirror-parity.test.ts` (server
 * side). Edit one copy without the other and exactly one of those two
 * tests fails with a clear diff — that's the drift signal, not the
 * `policy-templates.test.ts` reference-identity check (which only
 * proves the SERVER catalog re-exports `DEFAULT_POLICY_RULES`; it has
 * no visibility into this file at all).
 */

import type { Rule, RuleCategory, RuleId, RuleType } from '@sill/types';

import { rulesFingerprint } from './helpers.js';

/** Version label ADR-0080's seed stamps on the active policy row. */
export const BASELINE_POLICY_VERSION = 'v1-default';

/** Role gate — mirrors `can_publish` in Guardrails.tsx. */
export type CanTemplateLoadRole = 'owner' | 'admin' | 'reviewer' | 'viewer' | null;

/**
 * The eight ADR-0080 baseline rules. Locally mirrored so the dashboard
 * can compute the baseline fingerprint without a server round-trip on
 * every render.
 *
 * Any edit here without the same edit in
 * `services/api/src/sites/default-policy.ts` breaks the CI parity test
 * (`services/api/test/sites/policy-templates.test.ts`).
 */
const BASELINE_RULES: readonly Rule[] = [
  {
    rule_id: 'r03' as RuleId,
    type: 'r03' as RuleType,
    category: 'rate_limit' as RuleCategory,
    params: { type: 'r03' } as Rule['params'],
    action_on_match: 'reject',
    order: 1,
    enabled: true,
  },
  {
    rule_id: 'r04' as RuleId,
    type: 'r04' as RuleType,
    category: 'rate_limit' as RuleCategory,
    params: { type: 'r04' } as Rule['params'],
    action_on_match: 'reject',
    order: 2,
    enabled: true,
  },
  {
    rule_id: 'r09' as RuleId,
    type: 'r09' as RuleType,
    category: 'dark_pattern' as RuleCategory,
    params: { type: 'r09' } as Rule['params'],
    action_on_match: 'reject',
    order: 3,
    enabled: true,
  },
  {
    rule_id: 'r10' as RuleId,
    type: 'r10' as RuleType,
    category: 'prompt_injection' as RuleCategory,
    params: { type: 'r10' } as Rule['params'],
    action_on_match: 'reject',
    order: 4,
    enabled: true,
  },
  {
    rule_id: 'r14' as RuleId,
    type: 'r14' as RuleType,
    category: 'spend' as RuleCategory,
    params: { type: 'r14' } as Rule['params'],
    action_on_match: 'reject',
    order: 5,
    enabled: true,
  },
  {
    rule_id: 'r15' as RuleId,
    type: 'r15' as RuleType,
    category: 'spend' as RuleCategory,
    params: { type: 'r15' } as Rule['params'],
    action_on_match: 'reject',
    order: 6,
    enabled: true,
  },
  {
    rule_id: 'r20' as RuleId,
    type: 'r20' as RuleType,
    category: 'prompt_injection' as RuleCategory,
    params: { type: 'r20' } as Rule['params'],
    action_on_match: 'reject',
    order: 7,
    enabled: true,
  },
  {
    rule_id: 'r22' as RuleId,
    type: 'r22' as RuleType,
    category: 'prompt_injection' as RuleCategory,
    params: { type: 'r22' } as Rule['params'],
    action_on_match: 'reject',
    order: 8,
    enabled: true,
  },
];

/**
 * Fingerprint of the ADR-0080 baseline rules using the existing
 * `rulesFingerprint` helper (the same helper `draftStateFor` uses to
 * classify draft edits). Cached at module load — pure constant.
 */
export const BASELINE_FINGERPRINT: string = rulesFingerprint(BASELINE_RULES);

/**
 * Inputs to the CTA-visibility check. Kept a small explicit record
 * so the helper is trivially unit-testable without mounting the view.
 */
export interface TemplateCtaVisibilityInput {
  /** Rules currently in the merchant's draft — length is the "empty" signal. */
  readonly draft_rules: readonly Rule[];
  /** Active policy version label, or null when no active policy exists. */
  readonly active_policy_version: string | null;
  /**
   * Active policy rules, or null when no active policy exists. Used
   * to compute the fingerprint we compare to `BASELINE_FINGERPRINT`.
   */
  readonly active_policy_rules: readonly Rule[] | null;
  /** Caller's role. Only owner/admin see the CTA. */
  readonly role: CanTemplateLoadRole;
}

/**
 * Should the "Load a starter template" CTA render in the empty-state
 * card? Applies the ADR-0177 §"Empty-state CTA" rule.
 */
export function shouldShowTemplateCta(
  input: TemplateCtaVisibilityInput,
): boolean {
  // Guard (c) — role first: a reviewer/viewer never sees the CTA even
  // if the draft is empty and the policy is baseline. Fast rejection.
  if (input.role !== 'owner' && input.role !== 'admin') return false;
  // Guard (a) — DRAFT must be empty.
  if (input.draft_rules.length > 0) return false;
  // Guard (b) — no active policy, an active policy with zero rules
  // (blank guardrails — the merchant published their draft back down
  // to nothing), OR the untouched baseline.
  if (input.active_policy_version === null || input.active_policy_rules === null) {
    return true;
  }
  if (input.active_policy_rules.length === 0) return true;
  if (input.active_policy_version !== BASELINE_POLICY_VERSION) return false;
  return rulesFingerprint(input.active_policy_rules) === BASELINE_FINGERPRINT;
}
